var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import cx from 'classnames';
import * as React from 'react';
import * as _ from 'lodash';
import { FelaTheme } from 'react-fela';
import callable from './callable';
import felaRenderer from './felaRenderer';
import getClasses from './getClasses';
import getElementType from './getElementType';
import getUnhandledProps from './getUnhandledProps';
import logProviderMissingWarning from './providerMissingHandler';
import { FocusZoneMode, } from './accessibility/types';
import { defaultBehavior } from './accessibility';
import getKeyDownHandlers from './getKeyDownHandlers';
import { mergeComponentStyles, mergeComponentVariables } from './mergeThemes';
import { FocusZone as FabricFocusZone } from './accessibility/FocusZone';
import { FOCUSZONE_WRAP_ATTRIBUTE } from './accessibility/FocusZone/focusUtilities';
import createAnimationStyles from './createAnimationStyles';
var getAccessibility = function (props, actionHandlers, isRtlEnabled) {
    var customAccessibility = props.accessibility, defaultAccessibility = props.defaultAccessibility;
    var accessibility = (customAccessibility ||
        defaultAccessibility ||
        defaultBehavior)(props);
    var keyHandlers = getKeyDownHandlers(actionHandlers, accessibility.keyActions, props, isRtlEnabled);
    return __assign({}, accessibility, { keyHandlers: keyHandlers });
};
/**
 * This function provides compile-time type checking for the following:
 * - if FocusZone implements FocusZone interface,
 * - if FocusZone properties extend FocusZoneProps, and
 * - if the passed properties extend FocusZoneProps.
 *
 * Should the FocusZone implementation change at any time, this function should provide a compile-time guarantee
 * that the new implementation is backwards compatible with the old implementation.
 */
function wrapInGenericFocusZone(FocusZone, props, children, ref) {
    props[FOCUSZONE_WRAP_ATTRIBUTE] = true;
    return (React.createElement(FocusZone, __assign({ ref: ref }, props), children));
}
var renderWithFocusZone = function (render, focusZoneDefinition, config, focusZoneRef) {
    if (focusZoneDefinition.mode === FocusZoneMode.Wrap) {
        return wrapInGenericFocusZone(FabricFocusZone, __assign({}, focusZoneDefinition.props, { isRtl: config.rtl }), render(config), focusZoneRef);
    }
    if (focusZoneDefinition.mode === FocusZoneMode.Embed) {
        var originalElementType = config.ElementType;
        config.ElementType = FabricFocusZone;
        config.rest = __assign({}, config.rest, focusZoneDefinition.props);
        config.rest.as = originalElementType;
        config.rest.ref = focusZoneRef;
        config.rest.isRtl = config.rtl;
    }
    return render(config);
};
var renderComponent = function (config) {
    var className = config.className, defaultProps = config.defaultProps, displayName = config.displayName, handledProps = config.handledProps, props = config.props, state = config.state, actionHandlers = config.actionHandlers, focusZoneRef = config.focusZoneRef, render = config.render;
    return (React.createElement(FelaTheme, { render: function (theme) {
            if (_.isEmpty(theme)) {
                logProviderMissingWarning();
            }
            var _a = theme.siteVariables, siteVariables = _a === void 0 ? {
                colors: {},
                contextualColors: {},
                emphasisColors: {},
                naturalColors: {},
                fontSizes: {},
            } : _a, _b = theme.componentVariables, componentVariables = _b === void 0 ? {} : _b, _c = theme.componentStyles, componentStyles = _c === void 0 ? {} : _c, _d = theme.rtl, rtl = _d === void 0 ? false : _d, _e = theme.renderer, renderer = _e === void 0 ? felaRenderer : _e;
            var ElementType = getElementType({ defaultProps: defaultProps }, props);
            var stateAndProps = __assign({}, state, props);
            // Resolve variables for this component, allow props.variables to override
            var resolvedVariables = mergeComponentVariables(componentVariables[displayName], props.variables)(siteVariables, stateAndProps);
            var animationCSSProp = props.animation
                ? createAnimationStyles(props.animation, theme)
                : {};
            // Resolve styles using resolved variables, merge results, allow props.styles to override
            var mergedStyles = mergeComponentStyles(componentStyles[displayName], {
                root: props.styles,
            });
            var accessibility = getAccessibility(stateAndProps, actionHandlers, rtl);
            var rest = getUnhandledProps({ handledProps: handledProps.concat(accessibility.handledProps) }, props);
            var styleParam = {
                props: stateAndProps,
                variables: resolvedVariables,
                theme: theme,
            };
            mergedStyles.root = __assign({}, callable(mergedStyles.root)(styleParam), animationCSSProp);
            var resolvedStyles = Object.keys(mergedStyles).reduce(function (acc, next) {
                var _a;
                return (__assign({}, acc, (_a = {}, _a[next] = callable(mergedStyles[next])(styleParam), _a)));
            }, {});
            var classes = getClasses(renderer, mergedStyles, styleParam);
            classes.root = cx(className, classes.root, props.className);
            var config = {
                ElementType: ElementType,
                rest: rest,
                classes: classes,
                variables: resolvedVariables,
                styles: resolvedStyles,
                accessibility: accessibility,
                rtl: rtl,
                theme: theme,
            };
            if (accessibility.focusZone) {
                return renderWithFocusZone(render, accessibility.focusZone, config, focusZoneRef);
            }
            return render(config);
        } }));
};
export default renderComponent;
